import type { ReactNode } from 'react';
import { SidebarRow } from '../SidebarRow/SidebarRow';
import styles from './SidebarItem.module.css';

/**
 * Figma: `_sidebar item` (48:628) — "Primary navigation link.
 * Variants: State=Default | Active | Hover. Properties: Icon (instance swap),
 * Counter (toggle), Badge (toggle). Supports icon swap for each
 * navigation destination."
 *
 * The `_` prefix marks it internal in Figma, so it is not part of the public
 * package surface — compose it through `Sidebar`.
 */
export type SidebarItemState = 'default' | 'hover' | 'active';

export type SidebarItemProps = {
  label: string;
  icon?: ReactNode;
  counter?: ReactNode;
  badge?: ReactNode;
  href?: string;
  onClick?: () => void;
  /**
   * Normally leave this alone: hover is handled by CSS and `active` by the
   * `active` prop. Forcing a state is for documentation and visual regression,
   * where all three variants need to render side by side.
   */
  state?: SidebarItemState;
  className?: string;
};

export function SidebarItem({ label, icon, counter, badge, href, onClick, state, className }: SidebarItemProps) {
  const active = state === 'active';
  const surface = [
    styles.surface,
    active ? styles.active : null,
    state === 'hover' ? styles.hover : null,
    state === undefined ? styles.interactive : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <SidebarRow
      label={label}
      icon={icon}
      counter={counter}
      badge={badge}
      href={href}
      active={active}
      onClick={onClick}
      surfaceClassName={surface}
      className={className}
    />
  );
}
